import { Link, Spinner, Tabs, Tab } from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import useSegment from '@/hooks/useSegment';
import useTrackers from '@/patient-app-common/hooks/useTrackers';
import useResults from '@/patient-app-common/hooks/useResults';
import useSurveys from '@/patient-app-common/hooks/useSurveys';

import { TrackerEntry } from './TrackerEntry';
import { TrackerResult } from './TrackerResult';

const EVENT = 'Past survey viewed';

export function TrackerTabs() {
  const { t } = useTranslation('progress');
  const { sendTrackEvent } = useSegment();
  const { trackerId } = useParams();
  const { createResults } = useResults();

  const { getSurveyEntryById, isLoading: isLoadingEntries } = useTrackers();
  const { allSurveys, allMetrics, isLoading: isLoadingSurveys } = useSurveys();
  const isLoading = isLoadingEntries || isLoadingSurveys;

  const entry = getSurveyEntryById(_.toNumber(trackerId));

  const [tab, setTab] = useState('answers');

  const [recommendations, setRecommendations] = useState(null);

  const survey = useMemo(() => {
    if (!entry || !allSurveys || !allMetrics) return null;
    return _.find(allSurveys.concat(allMetrics), ['id', entry.procedureId]);
  }, [entry, allMetrics, allSurveys]);

  useEffect(() => {
    if (!entry?.result || !survey) return;

    const parseRecommendations = async () => {
      const results = await createResults(survey, entry);
      setRecommendations(results);
    };

    parseRecommendations();
  }, [survey, entry]);

  useEffect(() => {
    if (!entry || !survey) return;

    sendTrackEvent(EVENT, {
      entryDate: dayjs(entry.date).format('YYYY-MM-DD').toString(),
      procedureId: survey.id,
      procedureName: survey.patientDisplayName,
      tab,
    });
  }, [survey, entry, tab]);

  return (
    <div className="flex flex-col mx-auto w-full md:w-1/2 gap-4 m-4">
      <Link href="/progress?tab=surveys">
        <Icon icon="mdi:chevron-left" className="inline-flex" />
        {t('common.action.back')}
      </Link>
      {isLoading && <Spinner size="lg" />}
      <Tabs
        color="primary"
        variant="bordered"
        selectedKey={tab}
        onSelectionChange={setTab}
      >
        <Tab key="answers" title={t('tracker.tab.answers')}>
          <TrackerEntry entry={entry} survey={survey} />
        </Tab>
        <Tab
          key="results"
          title={t('tracker.tab.results')}
          isDisabled={_.isEmpty(recommendations)}
        >
          <TrackerResult recommendations={recommendations} />
        </Tab>
      </Tabs>
    </div>
  );
}
